import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { MoneyInput } from '@/components/money/MoneyInput';
import { RateInput } from '@/components/money/RateInput';
import { apiErrorMessage } from '@/lib/api';
import { useCreateDebt, useDebt, useUpdateDebt } from '@/hooks/useDebts';
import { DEBT_TYPE_LABELS } from '@shared/labels';
import type { DebtType, InterestRateType } from '@shared/types';

export function DebtFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const debtId = id ? Number(id) : undefined;
  const isEdit = debtId != null;

  const { data: existingDebt } = useDebt(debtId);
  const createDebt = useCreateDebt();
  const updateDebt = useUpdateDebt(debtId ?? 0);
  const isPending = isEdit ? updateDebt.isPending : createDebt.isPending;

  const [name, setName] = useState('');
  const [creditor, setCreditor] = useState('');
  const [type, setType] = useState<DebtType>('REVOLVING');
  const [principal, setPrincipal] = useState(0);
  const [ratePercent, setRatePercent] = useState(0);
  const [rateType, setRateType] = useState<InterestRateType>('EA');
  const [termMonths, setTermMonths] = useState<number | ''>('');
  const [creditLimit, setCreditLimit] = useState(0);
  const [minimumPayment, setMinimumPayment] = useState(0);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (!existingDebt) return;
    setName(existingDebt.name);
    setCreditor(existingDebt.creditor ?? '');
    setType(existingDebt.type);
    setPrincipal(existingDebt.principal);
    setRatePercent(existingDebt.interestRate * 100);
    setRateType(existingDebt.interestRateType);
    setTermMonths(existingDebt.termMonths ?? '');
    setCreditLimit(existingDebt.creditLimit ?? 0);
    setMinimumPayment(existingDebt.minimumPayment ?? 0);
    setStartDate(existingDebt.startDate);
  }, [existingDebt]);

  const isFixed = type === 'FIXED_INSTALLMENT';

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-xl font-semibold">{isEdit ? 'Editar deuda' : 'Nueva deuda'}</h1>
        <p className="text-sm text-muted-foreground">Tarjeta de crédito, crédito de carro, o cualquier deuda con interés.</p>
      </div>

      <form
        className="card space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name || principal <= 0 || ratePercent < 0) return;
          if (isFixed && !termMonths) {
            toast.error('Una deuda de cuota fija necesita el plazo en meses');
            return;
          }
          const payload = {
            name,
            creditor: creditor || null,
            type,
            principal,
            interestRate: ratePercent / 100,
            interestRateType: rateType,
            termMonths: isFixed ? Number(termMonths) : null,
            creditLimit: creditLimit > 0 ? creditLimit : null,
            minimumPayment: minimumPayment > 0 ? minimumPayment : null,
            startDate,
          };
          try {
            if (isEdit) {
              await updateDebt.mutateAsync(payload);
              toast.success('Deuda actualizada');
              navigate(`/deudas/${debtId}`);
            } else {
              const debt = await createDebt.mutateAsync(payload);
              toast.success('Deuda creada');
              navigate(`/deudas/${debt.id}`);
            }
          } catch (err) {
            toast.error(apiErrorMessage(err, isEdit ? 'No se pudo actualizar la deuda' : 'No se pudo crear la deuda'));
          }
        }}
      >
        <div>
          <label className="text-xs font-medium text-muted-foreground">Nombre</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Visa Bancolombia" required />
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Entidad (opcional)</label>
          <Input value={creditor} onChange={(e) => setCreditor(e.target.value)} placeholder="Bancolombia" />
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Tipo de deuda</label>
          <Select value={type} onChange={(e) => setType(e.target.value as DebtType)}>
            {Object.entries(DEBT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Saldo actual</label>
          <MoneyInput value={principal} onChange={setPrincipal} />
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Tasa de interés</label>
          <RateInput ratePercent={ratePercent} rateType={rateType} onChangeRate={setRatePercent} onChangeType={setRateType} />
        </div>

        {isFixed && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">Plazo (meses)</label>
            <Input
              type="number"
              min={1}
              value={termMonths}
              onChange={(e) => setTermMonths(e.target.value ? Number(e.target.value) : '')}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Cupo máximo (opcional)</label>
            <MoneyInput value={creditLimit} onChange={setCreditLimit} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Pago mínimo (opcional)</label>
            <MoneyInput value={minimumPayment} onChange={setMinimumPayment} />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Fecha de inicio</label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>

        <Button type="submit" className="w-full" disabled={isPending}>
          {isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear deuda'}
        </Button>
      </form>
    </div>
  );
}
